import { useQuery } from "@tanstack/react-query";
import { CalendarClock, Clock, Mail, Phone, ShieldCheck, UserMinus, UserPlus } from "lucide-react";
import { type ComponentType, useEffect, useMemo, useState } from "react";

import {
  getTeamDeals,
  getTeamMember,
  getTrainingTeamReport,
  type DealStatus,
  type ManagerDealListItem,
  type TeamMemberDetail,
  type TrainingTeamReportItem,
} from "@/api/client";
import { PoDealLedger } from "@/components/po-insights/PoDealLedger";
import { StatCard } from "@/components/StatCard";
import { CERTIFICATION_BADGE, deriveCertification } from "@/components/training/certification";
import { DealDetailDrawer } from "@/components/deals/DealDetailDrawer";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DetailSections, SectionHeading } from "@/components/ui/section";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { statusBadgeVariant } from "@/components/po-insights/team-status";

export interface TeamMemberDrawerProps {
  /** The member to show; null closes the drawer. */
  userId: string | null;
  token: string;
  onClose: () => void;
}

/** Review-r2: status filter chips above the member's deal ledger. Each maps to a
 * set of deal_status values; "all" shows everything. */
type MemberDealStatusFilter = "all" | "live" | "in_review" | "closed_won" | "closed_lost";
const MEMBER_DEAL_STATUS_CHIPS: { value: MemberDealStatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "live", label: "Live" },
  { value: "in_review", label: "In review" },
  { value: "closed_won", label: "Closed-won" },
  { value: "closed_lost", label: "Closed-lost" },
];
const MEMBER_DEAL_STATUS_SET: Record<
  Exclude<MemberDealStatusFilter, "all">,
  readonly DealStatus[]
> = {
  live: ["live"],
  in_review: ["pending_review", "extension_pending", "win_pending_review"],
  closed_won: ["closed_won"],
  closed_lost: ["closed_lost", "denied"],
};

/** Initials for the avatar fallback, from name or email. */
function initials(member: { full_name: string | null; email: string | null }): string {
  const source = member.full_name?.trim() || member.email || "?";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

/** History event-code → icon. Unknown codes fall back to a generic clock. */
const HISTORY_ICON: Record<string, ComponentType<{ className?: string }>> = {
  invited: UserPlus,
  role_changed: ShieldCheck,
  deactivated: UserMinus,
  removed: UserMinus,
  reactivated: UserPlus,
  status_changed: Clock,
};

function DetailRow({
  icon: Icon,
  label,
  value,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="break-words text-sm">{value}</div>
      </div>
    </div>
  );
}

/**
 * Member profile slide-out (Flow F). A 65vw right Sheet showing the member's
 * identity banner, per-member deal metrics + ledger + certification, the
 * HISTORY & AUDIT timeline built from `detail.history`, and the contact +
 * onboarding details. Read-only — lifecycle actions live on the directory row's
 * Manage menu (the page re-fetches the row + detail after a mutation).
 *
 * CHP-538: titled by the member's name, sections separated by hairline dividers
 * (DetailSections) with eyebrow SectionHeadings, not nested boxes.
 *
 * Review-r2: the four metric cards mirror the org cards; the deal ledger gets
 * status filter chips + drops the redundant Owner column; the role reads as
 * plain text; and the contact box sits at the BOTTOM (8a3c63e4 / 4edf8928 /
 * 23ab33c3 / 29dccdfe).
 */
export function TeamMemberDrawer({ userId, token, onClose }: TeamMemberDrawerProps) {
  const detailQuery = useQuery<TeamMemberDetail>({
    queryKey: ["partners", "team-member", userId, token],
    queryFn: () => getTeamMember(token, userId!),
    enabled: !!token && !!userId,
  });
  const member = detailQuery.data;

  // CHP-497/499: the member view also surfaces THIS member's deal metrics + own
  // deal ledger + certification, derived from the org-wide feeds filtered to the
  // member (the same feeds the directory + Team Knowledge drawer use).
  const dealsQuery = useQuery<ManagerDealListItem[]>({
    queryKey: ["deals", "team-list", "all", token],
    queryFn: () => getTeamDeals(token, "all"),
    enabled: !!token && !!userId,
  });
  const trainingQuery = useQuery<TrainingTeamReportItem[]>({
    queryKey: ["training", "team-report"],
    queryFn: () => getTrainingTeamReport(token),
    enabled: !!token && !!userId,
  });

  const [detailDealId, setDetailDealId] = useState<string | null>(null);
  // Review-r2: status filter chips above this member's deal ledger.
  const [statusFilter, setStatusFilter] = useState<MemberDealStatusFilter>("all");

  // Reset the ledger filter whenever the drawer switches to a different member.
  useEffect(() => {
    setStatusFilter("all");
  }, [userId]);

  // This member's own deals (owner join key = lead_bdm_id).
  const memberDeals = useMemo(
    () => (dealsQuery.data ?? []).filter((d) => d.lead_bdm_id === userId),
    [dealsQuery.data, userId],
  );

  // Review-r2: the ledger respects the status filter chips ("all" = no filter).
  const visibleMemberDeals = useMemo(() => {
    if (statusFilter === "all") return memberDeals;
    const set = MEMBER_DEAL_STATUS_SET[statusFilter];
    return memberDeals.filter((d) => set.includes(d.deal_status));
  }, [memberDeals, statusFilter]);

  // Calendar-YTD boundary (UTC Jan 1), matching the org YTD aggregates so the
  // "Closed YTD" card labels are honest (was unbounded all-time).
  const yearStartMs = useMemo(() => Date.UTC(new Date().getUTCFullYear(), 0, 1), []);

  // CHP-499: the four per-member metric cards — # Live deals, # Pending deals,
  // # Closed-won YTD, # Closed-lost YTD — as counts, not dollar totals.
  // "Closed YTD" windows on closed_at >= this calendar year's start.
  const memberMetrics = useMemo(() => {
    const closedThisYtd = (d: (typeof memberDeals)[number]) =>
      d.closed_at != null && new Date(d.closed_at).getTime() >= yearStartMs;
    return {
      liveCount: memberDeals.filter((d) => d.deal_status === "live").length,
      pending: memberDeals.filter(
        (d) => d.deal_status === "pending_review" || d.deal_status === "win_pending_review",
      ).length,
      wonCount: memberDeals.filter((d) => d.deal_status === "closed_won" && closedThisYtd(d))
        .length,
      lostCount: memberDeals.filter((d) => d.deal_status === "closed_lost" && closedThisYtd(d))
        .length,
    };
  }, [memberDeals, yearStartMs]);

  const memberTraining = useMemo(
    () => (trainingQuery.data ?? []).find((t) => t.user_id === userId) ?? null,
    [trainingQuery.data, userId],
  );

  return (
    <Sheet
      open={!!userId}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:w-[65vw] sm:max-w-[65vw]"
        data-testid="team-member-drawer"
      >
        {/* CHP-538 / review-r2 (8a3c63e4): the page header IS the member's name
            — the generic "Team member / Identity…" title/description was removed. */}
        <SheetHeader>
          <SheetTitle className="pr-8">
            {member?.full_name?.trim() || member?.email || "Team member"}
          </SheetTitle>
        </SheetHeader>

        {detailQuery.isLoading ? (
          <p role="status" className="mt-6 text-sm text-muted-foreground">
            Loading member…
          </p>
        ) : detailQuery.error || !member ? (
          <p role="alert" className="mt-6 text-sm">
            Couldn&apos;t load this member.
          </p>
        ) : (
          <DetailSections className="mt-6">
            {/* Identity banner — name is already the page title, so this row
                carries the avatar, role (plain text, review-r2) + status. */}
            <div className="flex items-center gap-4">
              <Avatar className="h-12 w-12">
                <AvatarFallback>{initials(member)}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                {/* CHP-538: name is the sheet title; the banner carries the
                    avatar + role/status + position (no duplicate name). */}
                <div className="flex flex-wrap items-center gap-2">
                  {/* Review-r2: role is plain text, not a pill. */}
                  <span className="text-sm font-medium">{member.role_label}</span>
                  <Badge variant={statusBadgeVariant(member.status)} dot>
                    {member.status_label}
                  </Badge>
                </div>
                {member.position || member.company_name ? (
                  <p className="text-sm text-muted-foreground">
                    {[member.position, member.company_name].filter(Boolean).join(" · ")}
                  </p>
                ) : null}
              </div>
            </div>

            {/* CHP-499: per-member metric cards — # Live deals, # Pending deals,
                # Closed-won YTD, # Closed-lost YTD (counts, not dollar totals). */}
            <section>
              <SectionHeading className="mb-3">Deal activity</SectionHeading>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <StatCard
                  title="Live deals"
                  value={memberMetrics.liveCount}
                  tone="neutral"
                  testId="member-live-deals"
                />
                <StatCard
                  title="Pending deals"
                  value={memberMetrics.pending}
                  tone="neutral"
                  testId="member-pending-deals"
                />
                <StatCard
                  title="Closed-won YTD"
                  value={memberMetrics.wonCount}
                  tone="success"
                  testId="member-closed-won"
                />
                <StatCard
                  title="Closed-lost YTD"
                  value={memberMetrics.lostCount}
                  tone="neutral"
                  testId="member-closed-lost"
                />
              </div>
            </section>

            {/* CHP-499 + review-r2: this member's own deal ledger, with status
                filter chips (top-left) and no Owner column (single owner). */}
            <section>
              <SectionHeading className="mb-3">Deals</SectionHeading>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                {MEMBER_DEAL_STATUS_CHIPS.map((chip) => (
                  <Button
                    key={chip.value}
                    type="button"
                    size="sm"
                    variant={statusFilter === chip.value ? "default" : "outline"}
                    onClick={() => setStatusFilter(chip.value)}
                    data-testid={`member-deal-filter-${chip.value}`}
                  >
                    {chip.label}
                  </Button>
                ))}
              </div>
              {dealsQuery.isLoading ? (
                <p role="status" className="text-sm text-muted-foreground">
                  Loading deals…
                </p>
              ) : (
                <PoDealLedger
                  deals={visibleMemberDeals}
                  onDealClick={setDetailDealId}
                  hideOwnerColumn
                  emptyMessage={
                    statusFilter === "all"
                      ? "No deals owned by this member yet."
                      : "No deals match this filter."
                  }
                />
              )}
            </section>

            {/* CHP-499: this member's certification details + # additional
                modules (from the training report). */}
            <section>
              <SectionHeading className="mb-3">Training &amp; certification</SectionHeading>
              {trainingQuery.isLoading ? (
                <p role="status" className="text-sm text-muted-foreground">
                  Loading training…
                </p>
              ) : memberTraining ? (
                <div
                  className="flex flex-wrap items-center gap-4 rounded-lg border p-4"
                  data-testid="member-certification"
                >
                  <div>
                    <div className="text-xs text-muted-foreground">Certification</div>
                    <Badge
                      className={`mt-1 ${CERTIFICATION_BADGE[deriveCertification(memberTraining)].className ?? ""}`}
                      variant={CERTIFICATION_BADGE[deriveCertification(memberTraining)].variant}
                    >
                      {CERTIFICATION_BADGE[deriveCertification(memberTraining)].label}
                    </Badge>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground"># Optional modules</div>
                    <div
                      className="mt-1 text-sm tabular-nums"
                      data-testid="member-additional-modules"
                    >
                      {memberTraining.extra_training}
                    </div>
                  </div>
                  {memberTraining.exempted ? (
                    <Badge variant="secondary">Exempt (still owed)</Badge>
                  ) : null}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No training record for this member.</p>
              )}
            </section>

            {/* History & audit timeline */}
            <section>
              <SectionHeading>History &amp; audit</SectionHeading>
              {member.history.length === 0 ? (
                <p className="mt-2 text-sm text-muted-foreground">No recorded activity yet.</p>
              ) : (
                <ol className="mt-3 space-y-4" data-testid="team-member-history">
                  {member.history.map((entry, i) => {
                    const Icon = HISTORY_ICON[entry.event] ?? Clock;
                    return (
                      <li key={`${entry.at}-${i}`} className="flex gap-3">
                        <span
                          aria-hidden
                          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"
                        >
                          <Icon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm">{entry.summary}</div>
                          <div className="text-xs text-muted-foreground">
                            {formatDateTime(entry.at)}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>

            {/* Review-r2 (8a3c63e4): the contact + onboarding details moved to
                the BOTTOM of the page (kept in CHP-538's eyebrow-section idiom). */}
            <section>
              <SectionHeading className="mb-3">Contact &amp; onboarding</SectionHeading>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DetailRow icon={Mail} label="Email" value={member.email ?? "—"} />
                <DetailRow icon={Phone} label="Phone" value={member.phone ?? "—"} />
                <DetailRow
                  icon={CalendarClock}
                  label="Invited"
                  value={formatDate(member.invited_at)}
                />
                <DetailRow
                  icon={ShieldCheck}
                  label="Onboarded"
                  value={
                    member.onboarded_at ? formatDate(member.onboarded_at) : "Not yet onboarded"
                  }
                />
                <DetailRow
                  icon={Clock}
                  label="Last active"
                  value={formatDate(member.last_active_at)}
                />
                {member.deactivated_at ? (
                  <DetailRow
                    icon={UserMinus}
                    label="Removed"
                    value={formatDate(member.deactivated_at)}
                  />
                ) : null}
              </div>
            </section>
          </DetailSections>
        )}

        {/* Master-record detail, layered over the member drawer (row click).
            CHP-808: read-only — the PO is viewing a team member's deal, not their
            own, so owner-only write actions are suppressed (the backend 404s a
            non-owner on the note/win/loss endpoints). */}
        <DealDetailDrawer dealId={detailDealId} onClose={() => setDetailDealId(null)} readOnly />
      </SheetContent>
    </Sheet>
  );
}
